import { ACCESS_CATALOG, TOTAL_CELLS } from "../constants/accessCatalog.constants";
import type {
  AccessRole,
  AtomicAction,
  CellResolution,
  EffectiveSummary,
  GatheredStatement,
  StatementAction,
} from "../types/access.types";

/**
 * Client mirror of the backend RBAC engine (`authorization.service.ts` + `action-semantics.ts`). The backend
 * exposes no "effective permissions" endpoint, so the deny-first resolution is replicated here for the
 * matrix, the effective-access counts and the simulator. Pure functions — no I/O.
 *
 * Scope (`organization_id`) is INTENTIONALLY ignored during resolution, mirroring the backend exactly. In
 * `authorization.service.ts` (`evaluateAtom`) the decision is made purely on `resource` + expanded action
 * with deny-first semantics; `isAuthorized()` receives no organization context and `resolvePrincipalRoles()`
 * flattens every assigned role's policies without filtering by tenancy. `organization_id` is a
 * management/visibility axis (who can see/edit/assign which roles and policies — see `scopeOf`,
 * `visibilityWhere`, `resolveVisiblePolicies`, `resolveAssignableRoles` in `coloca-nest`), NOT an evaluation
 * axis: once a role is assigned to a principal, its effect applies unconditionally. Because a principal only
 * ever accrues its own-org + global roles, no single principal mixes two distinct orgs. Making this preview
 * scope-aware would DIVERGE from real enforcement and give a false sense of security on a security-sensitive
 * screen — so resolution stays scope-agnostic, faithful to the engine. (Reviewed against backend
 * `feature/rbac`; PR #157 review Finding #1 ruled a false positive.)
 *
 * The matching is exact and case-sensitive on both `resource` and `action`, mirroring the backend
 * (`evaluateAtom` compares `statement.resource === resource`; there is no wildcard or parent→child
 * inheritance — `cards` and `cards:topup` are independent resources). The backend validates every persisted
 * statement against the `Module` / `Action` enums (`@IsEnum` in `PolicyStatementDto`), so an unknown resource
 * or action cannot exist today and this catalog is kept in sync by hand. The dev-only drift guards below make
 * a FUTURE contract drift (a server-side module or verb added without updating this mirror) observable
 * instead of silently fail-open — a dropped statement would otherwise render as `allow`/`none`. They never
 * change resolution for valid data. (PR #157 review Finding #4.)
 */

/** Catalog resource ids, for the dev drift guard in {@link gatherStatements}. */
const CATALOG_RESOURCE_IDS = new Set<string>(ACCESS_CATALOG.map((mod) => mod.id));

/** Tokens already warned about, so the per-cell/per-statement hot path logs each drift at most once. */
const warnedDriftTokens = new Set<string>();

/**
 * Dev-only, deduped warning for a value that the closed mirror does not recognize — the signal that the
 * frontend catalog/union has drifted from the backend contract. No-op in production builds.
 */
function warnDriftOnce(token: string, message: string): void {
  if (!import.meta.env.DEV || warnedDriftTokens.has(token)) return;
  warnedDriftTokens.add(token);
  console.warn(`[denyFirst] ${message}`);
}

/**
 * Collapse a statement action to the atomic actions it stands for: read→[read] · write/update→[write] ·
 * delete→[delete] · admin→[read,write,delete]. An unrecognized action expands to `[]` (the statement is
 * dropped) — safe for valid data since the backend rejects unknown actions, but logged in dev as a drift
 * signal: a dropped `deny` would otherwise silently render as `allow`/`none`.
 */
export function expandAction(action: StatementAction): AtomicAction[] {
  switch (action) {
    case "read":
      return ["read"];
    case "write":
    case "update":
      return ["write"];
    case "delete":
      return ["delete"];
    case "admin":
      return ["read", "write", "delete"];
    default:
      warnDriftOnce(
        `action:${String(action)}`,
        `Unknown action "${String(action)}" dropped — out of sync with the backend Action/CompositeAction enum? The statement is ignored (fail-open in this preview).`,
      );
      return [];
  }
}

/**
 * Flatten a principal's assigned roles → policies → statements, annotating each with its origin (role +
 * policy). Assigned role ids are resolved against the roles catalog (which carries the embedded policies).
 * Roles not present in the catalog are skipped — a single missing role never aborts the computation.
 */
export function gatherStatements(
  assignedRoleIds: readonly string[],
  rolesCatalog: readonly AccessRole[],
): GatheredStatement[] {
  const byId = new Map(rolesCatalog.map((role) => [role.id, role]));
  const out: GatheredStatement[] = [];
  for (const roleId of assignedRoleIds) {
    const role = byId.get(roleId);
    if (!role) continue;
    for (const policy of role.policies) {
      for (const statement of policy.statements) {
        if (!CATALOG_RESOURCE_IDS.has(statement.resource)) {
          warnDriftOnce(
            `resource:${statement.resource}`,
            `Resource "${statement.resource}" is not in ACCESS_CATALOG — out of sync with the backend Module enum? Its statements won't appear in the matrix/simulator (fail-open).`,
          );
        }
        out.push({
          resource: statement.resource,
          action: statement.action,
          effect: policy.effect,
          role,
          policy,
        });
      }
    }
  }
  return out;
}

/**
 * Deny-first resolution for a single `(resource, atom)` cell. A statement matches when its resource equals
 * `resource` and its (possibly composite) action expands to include `atom`. Deny wins over allow; with no
 * match the cell is `none` (default-deny).
 */
export function resolveCell(
  statements: readonly GatheredStatement[],
  resource: string,
  atom: AtomicAction,
): CellResolution {
  const matches = statements.filter((s) => s.resource === resource && expandAction(s.action).includes(atom));
  const denies = matches.filter((s) => s.effect === "deny");
  const allows = matches.filter((s) => s.effect === "allow");
  if (denies.length > 0) {
    return { effect: "deny", by: denies, overridden: allows };
  }
  if (allows.length > 0) {
    return { effect: "allow", by: allows, overridden: [] };
  }
  return { effect: "none", by: [], overridden: [] };
}

/**
 * Summarize the net effective access across the whole catalog: allowed cells, denied cells, and how many of
 * those denies overrode an allow. `total` is the catalog size — it feeds the "Acceso efectivo X/total" chip.
 */
export function summarize(statements: readonly GatheredStatement[]): EffectiveSummary {
  let allowed = 0;
  let denied = 0;
  let overrides = 0;
  for (const mod of ACCESS_CATALOG) {
    for (const atom of mod.actions) {
      const cell = resolveCell(statements, mod.id, atom);
      if (cell.effect === "allow") {
        allowed += 1;
      } else if (cell.effect === "deny") {
        denied += 1;
        if (cell.overridden.length > 0) overrides += 1;
      }
    }
  }
  return { allowed, denied, overrides, total: TOTAL_CELLS };
}
